import { useCallback, useContext, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Close,
    Heading,
    Text,
} from 'theme-ui';
import { Submit } from '~/Forms/Submit';
import { Field } from '~/Forms/Field';
import { Button, Alert as StyledAlert } from '~/Common';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { checkHin, storeEoiRegistrationPersonalDetails } from 'features/eoi/eoiAPI';
import { toast } from 'utils/toast';
import { connect, useDispatch } from 'react-redux';
import { Yup } from 'utils/validationRules';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { RegistrationDetailsContext } from '..';
import { dealOfferPriceFormatting, formatNumber } from 'utils/helpers';
import { getEoiUserAsync, setEoiUser } from 'features/auth/authSlice';
import RegistrationDetailsCard from '~/Common/RegistrationDetailsCard/RegistrationDetailsCard';

const mapStateToProps = (state) => {
    return { theme: state.theme }
}

const PersonalDetails = ({ dealInEoiPhase }) => {
    const dispatch = useDispatch();
    const registration = useContext(RegistrationDetailsContext);
    const { eoiUser, loading, setLoading, nextStep } = registration;
    const { t } = useTranslation();
    useDocumentTitle(t('Registration Details'));
    const formText = {
        title: eoiUser?.whitelabel_page ? 'Priority Shareholder Application' : dealInEoiPhase ? 'Expression of Interest' : 'registrationDetails.form.basic.title',
        subtitle: dealInEoiPhase ? 'Enter your basic details and we will be in touch when the deal opens.' : 'registrationDetails.form.basic.subtitle',
        next: 'registrationDetails.form.basic.next'
    }
    const [similarDetails, setSimilarDetails] = useState(null);
    const minBidPrice = eoiUser?.min_bid_price ? formatNumber(eoiUser.min_bid_price) : 2000;

    const validationSchema = Yup.object().shape({
        first_name: Yup.string().required('required field'),
        last_name: Yup.string().required('required field'),
        mobile: Yup.string().required('required field'),
        email: Yup.string().email('invalid email').required('required field'),
        hin_number: Yup.string().nullable(),
        original_capital: Yup.number().required().min(minBidPrice, `The minimum investment amount is $${dealOfferPriceFormatting(minBidPrice)}`),
        accept_terms: (!eoiUser?.alternative_investment && !dealInEoiPhase) ? Yup.boolean().required('required field').oneOf([true], 'You must accept the terms to proceed') : Yup.boolean().notRequired(),
    });

    const handleSubmit = (values, actions) => {
        setLoading(true);
        const isUsingSimilarDetails = similarDetails !== null;
        storeEoiRegistrationPersonalDetails({
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            mobile: values.mobile,
            hin_number: values.hin_number,
            original_capital: values.original_capital,
            use_similar_details: isUsingSimilarDetails
        }, eoiUser.encrypted_uuid).then((response) => {
            toast.success(t('registrationDetails.form.editing.success'), {
                toastId: 'personal-success',
            });    
            dispatch(setEoiUser(response.data));
            dispatch(getEoiUserAsync(response.data.encrypted_uuid));
            nextStep(null, isUsingSimilarDetails ? 1 : 0);
            setLoading(false);
            actions.setSubmitting(false);
            actions.setStatus('success');
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('registrationDetails.form.editing.error'), {
                toastId: 'personal-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
            setLoading(false);
        });
    };

    const timeoutRef = useRef(null);

    const handleCheckHin = useCallback((value) => {
        if (!value || !eoiUser?.uuid) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            checkHin(value, eoiUser.uuid).then((response) => {
                if (response.similarDetails) {
                    setSimilarDetails(response.similarDetails);
                } else {
                    setSimilarDetails(null);
                }
            }).catch(({ response }) => {
                setSimilarDetails(null);
            });
        }, 1000);
    }, [eoiUser.uuid]);

    return (
        <Box sx={{
            display: loading ? 'none' : 'block'
        }}>
            <Formik
                enableReinitialize
                initialValues={{
                    first_name: eoiUser?.first_name ?? '',
                    last_name: eoiUser?.last_name ?? '',
                    email: eoiUser?.email ?? '',
                    mobile: eoiUser?.mobile ?? '',
                    original_capital: eoiUser?.original_capital ?? 0,
                    hin_number: eoiUser?.hin_number ?? '',
                    accept_terms: false,
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ status, setStatus, setFieldValue, submitForm }) => (
                    <Box>
                        { status === 'api_error' ?
                        <Alert role="alert" variant='danger' mb={2}>
                            {t('registrationDetails.form.editing.error')}
                            <Close ml="auto" mr={-2} onClick={() => setStatus(null)} />
                        </Alert> : ''}

                        <Form>
                            <Heading as="h1" variant="h3" sx={{ fontWeight: 500 }} mb={4}>
                                {t(formText.title)}
                            </Heading>

                            <Text as="p" mb={3} sx={{
                                fontSize: 2,
                                color: 'dark'
                            }}>
                                {t(formText.subtitle)}
                            </Text>

                            <Heading as="h2" variant="form_section" sx={{
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                mb: 3,
                            }}>
                                {t('Your details')}
                            </Heading>

                            <Field
                                label="First name"
                                name="first_name"
                                required={true}
                            />

                            <Field
                                label="Last name"
                                name="last_name"
                                required={true}
                            />

                            <Field
                                label="Email"
                                name="email"
                                required={true}
                            />

                            <Field
                                label="Mobile"
                                name="mobile"
                                required
                            />

                            <Field
                                name="original_capital"
                                field="currency"
                                label={dealInEoiPhase ? "EOI amount" : "Investment amount"}
                                sx={{
                                    transition: 'font-size 0.5s ease-in-out',
                                }}
                                onKeyUp={e => setFieldValue('original_capital', e.target.value ? formatNumber(e.target.value.replaceAll(',', '').replace('$', '')) : 0)}
                            />

                            {(!eoiUser?.alternative_investment && !dealInEoiPhase) && (
                                <>
                                    <Field
                                        label="I understand that this form constitutes a Firm Bid for the Securities outlined in the Offer, in accordance with the Master ECM Terms."
                                        name="accept_terms"
                                        field="checkbox"
                                        required={true}
                                    />

                                    <Field
                                        label="HIN/SRN"
                                        name="hin_number"
                                        onKeyUp={(e) => handleCheckHin(e.target.value)}
                                    />
                                    <Text as="p" variant="small"   sx={{
                                        fontSize: '13px',
                                        color: 'dark',
                                        '& a': {
                                            color: 'tealDark'
                                        }
                                    }}>
                                        {t('[OPTIONAL] Nominate the HIN/SRN you would like these shares allocated to.')}
                                    </Text>
                                </>
                            )}

                            {similarDetails !== null ? (
                                <>
                                    <StyledAlert mt={4} mb={3} isCloseable={false} sx={{color: 'tealDark'}}>{t('We have details matching your HIN on file')}</StyledAlert>
                                    <RegistrationDetailsCard values={similarDetails.values} type={similarDetails.type} subtype={null} />

                                    <Submit variant="primary" id="registrationDetailsSubmit" text={t('Use the details above')} sx={{ display: 'block', width: '100%', mt: '40px' }} />

                                    <Button ml={3} sx={{ paddingY: '7px' }} variant="light" onClick={() => {
                                        setSimilarDetails(null);
                                        submitForm();
                                    }}>
                                        {t('Enter manually')}
                                    </Button>
                                </>
                            ) : (
                                <Submit variant="primary" id="registrationDetailsSubmit" text={t(formText.next)} sx={{ display: 'block', width: '100%', mt: '40px' }} />
                            )}
                        </Form>
                    </Box>
                )}
            </Formik>
        </Box>
    )
}

export default connect(mapStateToProps, null)(PersonalDetails);